import { useState, useEffect, createContext, useCallback, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Input, Button, Collapse, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  MultiSwitch,
  DraggableItem,
  VisitWindowModal,
  CopyVisitMappingModal,
  DraggablePeriod,
  AddFootnoteModal,
  DraggableVisit
} from "src/DigitalProtocolModule/components";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  SortableContext
} from "@dnd-kit/sortable";
import { useDispatch, useSelector } from "react-redux";
import { updateDPSoaActivities } from "src/DigitalProtocolModule/actions/createDP";
import "./index.scss";
import {
  mapConditions,
  updateActivities,
  addPeriodIDtoCondition,
  reOrderVisitsInPeriodType,
  addConditionByPeriod,
  incrementVisit,
  deleteConditionByPeriod,
  resetConditionByPeriod,
  renewCondition,
  handleDragData,
  reorderCondition,
  deleteConditionByVisit,
  getModalityOptions,
  removeDuplicates,
  filterDeletedItems,
  insertConditionByVisit,
  reorderConditionByVisit,
  resetConditionVisit,
  getWeeksOrderIndex,
  weekDayWindowMapping,
  getVisitWindowValues,
  addItem,
  addWindowItem,
  convertVisitWindow,
  checkOrder,
  findOutOfOrderIndices
} from "./utils";
import { deleteFootnoteDetail, saveSoaActivity } from "src/DigitalProtocolModule/utils/ajax-proxy";
import { toSentenceCase } from "src/utils/utils";
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { FOOTNOTE_GROUP } from "src/DigitalProtocolModule/constants";
import { useFootnoteHooks, useMarkAsCompleteHooks } from "src/DigitalProtocolModule/hooks";
import { fetchHistory } from "src/actions/historyTrial";
import styles from "./index.module.scss";

const { Panel } = Collapse;
interface Panel {
  key: string;
  type: string;
  data: any[];
}
interface SoaDetailsState {
  trial_id: string;
  user_name: string;
  tableItems: Digital_Protocol_SoA.LandingInfoType;
  dprot_id: string;  
  unit: string;
  winUnit: string
}

const LABS = "Labs";
const PHYSICAL_EXAMINATION = "Physical Examination";
const PROCEDURES = "Procedures";
const QUESTIONNAIRES = "Questionnaires";
const STUDY_PROCEDURES = "Study Procedures";

export function ScheduleActivitiesTable(props: any) {
  const location = useLocation();
  const history = useHistory();
  const locationState = location.state as SoaDetailsState;
  
  const finalizeStatus = useSelector(fetchHistory).data?.dpFinalizeStatus;

  const isLocked = useMemo(
    () => Boolean(finalizeStatus?.soa_finalize_flag),
    [finalizeStatus]
  );

  const {
    doFootnoteSoftDelete,
    doFootnoteHardSave,
    moveFootnoteByVisitChange,
    moveFootnoteByVisitMovement,
    moveFootnoteByPeriodChange,
    moveFootnoteByPeriodMovement,
    updatePreviewByPeriodVisit,
    checkIfExistFootnoteWarning,
    getFootnoteStateMergedWithSoftSave,
  } = useFootnoteHooks();  

  const { getSectionLockStatus } = useMarkAsCompleteHooks();

  const footnoteMergedDataState = getFootnoteStateMergedWithSoftSave()

  const existFootnoteWarning = useMemo(
    () => checkIfExistFootnoteWarning(footnoteMergedDataState),
    [footnoteMergedDataState]
  );

  const [unitIndex, setUnitIndex] = useState("Activity visit map");
  const [expandKeys, setExpandKeys] = useState(["1", "2", "3", "4", "5"]);
  const [visitTypes, setVisitTypes] = useState<
    Digital_Protocol_SoA_Table_Data.VisitType[]
  >([]);
  const [periodData, setPeriodData] = useState<
    Digital_Protocol_SoA_Table_Data.PeriodData[]
  >([]);
  const [visits, setVisits] = useState([]);
  const [weeks, setWeeks] = useState([]);  
  const [days, setDays]=useState([]);
  const [searchTxt, setSearchTxt] = useState<string>("");
  const [visitUnit] = useState(locationState?.unit);
  const [windowUnit] = useState<string>(locationState?.winUnit);

  const [labs, setLabs] = useState<Digital_Protocol_SoA_Table_Data.Entity[]>(
    []
  );
  const [examination, setExamination] = useState<
    Digital_Protocol_SoA_Table_Data.Entity[]
  >([]);
  const [procedures, setProcedures] = useState<
    Digital_Protocol_SoA_Table_Data.Entity[]
  >([]);
  const [questionnaire, setQuestionnaire] = useState<
    Digital_Protocol_SoA_Table_Data.Entity[]
  >([]);
  const [studyProcedures, setStudyProcedures] = useState<
    Digital_Protocol_SoA_Table_Data.Entity[]
  >([]);
  const [panelMapping, setPanelMapping] = useState<Panel[]>([]);

  const [modalityOptions, setModalityOptions] = useState<
    Digital_Protocol_SoA_Table_Data.ModalityOption[]
  >([]);
  const [whetherDraggable, setWhetherDraggable] =
    useState<Digital_Protocol_SoA_Table_Data.WhetherDraggable>({
      [LABS]: 0,
      [PHYSICAL_EXAMINATION]: 0,
      [PROCEDURES]: 0,
      [QUESTIONNAIRES]: 0,
      [STUDY_PROCEDURES]: 0,
    });
  const [visitWindowOpen, setVisitWindowOpen] = useState<Digital_Protocol_SoA_Table_Data.OpenVisitWindowType>({
    visible: false,
    visit: -1
  });
  const eventListHead = useRef();
  const [collapseWidth, setCollapseWidth] = useState(null);
  const [visitTobeMapped, setVisitToBeMapped] =
    useState<Digital_Protocol_SoA_Table_Data.Entity>();
  const [showVisitMappingModal, setShowVisitMappingModal] =
    useState<boolean>(false);
  const [activitiesMapped, setActivitiesMapped] = useState<
    Digital_Protocol_SoA_Table_Data.Entity[]
  >([]);
  const [periodsMapped, setPeriodsMapped] = useState<
    Digital_Protocol_SoA_Table_Data.PeriodData[]
  >([]);
  const [periodTobeMapped, setPeriodToBeMapped] =
    useState<Digital_Protocol_SoA_Table_Data.PeriodData>();
  const [isSaving, setIsSaving] = useState(false);
  const [whetherPeriodDraggable, setWhetherPeriodDraggable] = useState(0);
  const [dragPeriodIndex, setDragPeriodIndex] = useState({
    active: -1,
    over: -1,
    direction:'',
  });
  const [dragIndex, setDragIndex] = useState({
    active: -1,
    over: -1,
    direction: "",
  });
  const [isOpenAddFootnoteModal, setIsOpenAddFootnoteModal] = useState<Digital_Protocol_SoA_Table_Data.OpenFootnoteType>({
    visible: false
  })

  const [dragVisitIndex, setDragVisitIndex] = useState({
    active: -1,
    over: -1,
    direction:'',
  });
  const [whetherVisitDraggable, setWhetherVisitDraggable] = useState(0);
  const [weekVisitWindow, setWeekVisitWindow] = useState([])
  const [dayVisitWindow, setDayVisitWindow] = useState([])
  const getWidth = () => {
    const divElement: HTMLDivElement = eventListHead.current;
    return divElement?.offsetWidth;
  };

  const handleResize = () => {
    setCollapseWidth(getWidth());
  };

  useEffect(() => {
    if (eventListHead.current) {
      handleResize();
    }

    window.addEventListener("resize", handleResize);
    window.addEventListener("onclick", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("onclick", handleResize);
    };
  }, [eventListHead]);

  useEffect(() => {
    setTimeout(handleResize, 5500);
    // get finalize status
    getSectionLockStatus();
  }, []);

  useEffect(() => {
    handleResize();
  }, [periodData, visits]);

  const dispatch = useDispatch();
  const dpSoaState = useSelector((state: any) => state?.dpSoaReducer);

  const disabledSave = useMemo(() => {
    let isInvalid = false;
    // frequence validation
    isInvalid =
      isInvalid ||
      [
        ...dpSoaState.soaAddedExamination,
        ...dpSoaState.soaAddedLabs,
        ...dpSoaState.soaAddedProcedures,
        ...dpSoaState.soaAddedQuestionnaires,
        ...dpSoaState.soaAddedStudyProcedures,
      ]
        .reduce(
          (accumulator, currentValue) => {
            if (currentValue?.condition) {
              return [
                ...accumulator,
                ...currentValue?.condition,
              ]
            } else {
              return accumulator
            }
          },
          []
        )
        .filter(
          (item: Digital_Protocol_SoA_Table_Data.Condition) =>
            item.modality && item.modality_frequency
        )
        .some((item: Digital_Protocol_SoA_Table_Data.Condition) => {
          const value = parseInt(`${item.modality_frequency}`);
          return value < 1 || value > 15;
        });

    return isSaving || isInvalid;
  }, [dpSoaState, isSaving]);

  const applyConditions = (type, conditons) => {
    let activities = [];
    switch (type) {
      case "Labs":
        activities = conditons.filter((item) =>
          dpSoaState.soaPeriod_data
            .map((k) => k.period_id)
            .includes(item.period_id)
        );
        break;
      case "Examination":
        activities = conditons.filter((item) =>
          dpSoaState.soaPeriod_data
            .map((k) => k.period_id)
            .includes(item.period_id)
        );
        break;
      case "Procedures":
        activities = conditons.filter((item) =>
          dpSoaState.soaPeriod_data
            .map((k) => k.period_id)
            .includes(item.period_id)
        );
        break;
      case "Questionnaires":
        activities = conditons.filter((item) =>
          dpSoaState.soaPeriod_data
            .map((k) => k.period_id)
            .includes(item.period_id)
        );
        break;
      case "StudyProcedures":
        activities = conditons.filter((item) =>
          dpSoaState.soaPeriod_data
            .map((k) => k.period_id)
            .includes(item.period_id)
        );
        break;
      default:
        break;
    }
    return activities;
  };

  const getCondition = (category, cat) => {
    try {
      let tmpCategory = [category].slice(0)[0];
      let nVisits = dpSoaState?.soaVisits;
      let nWeeks = dpSoaState?.soaWeeks;
      let nDays = dpSoaState?.soaDays;      
      tmpCategory?.forEach((ele) => {
        let _periodType = reOrderVisitsInPeriodType(
          nVisits,
          dpSoaState.soaPeriod_data
        );
        const _array = _periodType?.flatMap((obj) =>
          Array(obj?.no_of_visits)?.fill(obj?.period_id)
        );
        if (
          !ele.condition ||
          ele.condition.length == 0 ||
          ele.condition.length < dpSoaState?.soaVisits?.length
        ) {
          let condition = [];
          nVisits?.forEach((e, idx) => {
            condition.push({
              visits: e,
              weeks: nWeeks && nWeeks[idx],
              days: nDays && nDays[idx],
              modality:
                ele?.condition?.length >= e
                  ? ele?.condition[e - 1]?.modality
                  : "",
              period_id:
                ele?.condition?.length >= e
                  ? ele?.condition[e - 1]?.period_id
                  : _array
                  ? _array[idx]
                  : null,
            });
          });
          const totalVisit = condition?.filter(
            (e) => e.modality && e.modality !== ""
          ).length;
          ele.condition = condition;
          ele.totalVisit = totalVisit;
        } else if (ele.condition.length > dpSoaState?.soaVisits?.length) {
          let condition = applyConditions(cat, ele.condition);
          const totalVisit = condition?.filter(
            (e) => e.modality && e.modality !== ""
          ).length;
          ele.condition = condition;
          ele.totalVisit = totalVisit;
        }
      });
      return tmpCategory;
    } catch (error) {
      console.error("Error in getCondition:", error);
      return null;
    }
  };

  useEffect(() => {
    if (
      (!dpSoaState?.soaAddedLabs ||
        !dpSoaState?.soaAddedExamination ||
        !dpSoaState?.soaAddedProcedures ||
        !dpSoaState?.soaAddedQuestionnaires ||
        !dpSoaState?.soaAddedStudyProcedures ||
        dpSoaState?.soaPeriod_data?.length == 0 ||
        !dpSoaState?.soaVisits ||
        dpSoaState?.soaVisits?.length == 0)
    )
      return;
    let tmpLabs = getCondition(dpSoaState?.soaAddedLabs, "Labs");
    let tmpExamination = getCondition(
      dpSoaState?.soaAddedExamination,
      "Examination"
    );
    let tmpProcedures = getCondition(
      dpSoaState?.soaAddedProcedures,
      "Procedures"
    );
    let tmpQuestionnaire = getCondition(
      dpSoaState?.soaAddedQuestionnaires,
      "Questionnaires"
    );
    let tmpStudyProcedures = getCondition(
      dpSoaState?.soaAddedStudyProcedures,
      "StudyProcedures"
    );
    setLabs(tmpLabs);
    setExamination(tmpExamination);
    setProcedures(tmpProcedures);
    setQuestionnaire(tmpQuestionnaire);
    setStudyProcedures(tmpStudyProcedures);

    dispatch(
      updateDPSoaActivities({
        soaAddedLabs: tmpLabs,
        soaAddedExamination: tmpExamination,
        soaAddedProcedures: tmpProcedures,
        soaAddedQuestionnaires: tmpQuestionnaire,
        soaAddedStudyProcedures: tmpStudyProcedures,
      })
    );
  }, [
    dpSoaState?.soaAddedLabs,
    dpSoaState?.soaAddedExamination,
    dpSoaState?.soaAddedProcedures,
    dpSoaState?.soaAddedQuestionnaires,
    dpSoaState?.soaAddedStudyProcedures,
    dpSoaState?.soaVisits,
    dpSoaState?.soaPeriod_data,
    dpSoaState?.soaWeeks,
    dpSoaState?.soaDays
  ]);

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 1,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor, pointerSensor);
  const noSensors = useSensors();

  const DragIndexContext = createContext({
    active: -1,
    over: -1,
    direction: "",
  });

  useEffect(() => {
    if (!searchTxt) {
      setPanelMapping([
        { key: "1", type: LABS, data: labs || [] },
        { key: "2", type: PHYSICAL_EXAMINATION, data: examination || [] },
        { key: "3", type: PROCEDURES, data: procedures || [] },
        { key: "4", type: QUESTIONNAIRES, data: questionnaire || [] },
        { key: "5", type: STUDY_PROCEDURES, data: studyProcedures || [] },
      ]);
    }
  }, [
    labs,
    examination,
    procedures,
    questionnaire,
    studyProcedures,
    searchTxt,
  ]);

  useEffect(() => {
    if (!dpSoaState.scenarios) return;

    const scenario = dpSoaState.scenarios[0];
    if (!scenario) return;

    const eventsConfigure = scenario["Schedule of Activities"];
    
    const {
      soaPeriod_data,
      soaVisits,
      soaWeeks,
      soaDays,
      soaAddedLabs,
      soaAddedExamination,
      soaAddedQuestionnaires,
      soaAddedProcedures,
      soaAddedStudyProcedures,
    } = dpSoaState;

    const tmpPeriodData =
      soaPeriod_data?.length === 0
        ? eventsConfigure?.period_data
        : soaPeriod_data;
    const visitDetails =
      soaVisits?.length === 0 ? eventsConfigure?.visits : soaVisits;

    const WeeksData = soaWeeks?.length === 0 ? eventsConfigure?.Weeks : soaWeeks;    
    const DaysData = soaDays?.length === 0 ? eventsConfigure?.Days : soaDays;

    const weekVisitWindow = weekDayWindowMapping(tmpPeriodData ?? {}, 'Week')
    const dayVisitWindow = weekDayWindowMapping(tmpPeriodData ?? {}, 'Day')    
    const updateValues = (addedItems, key) => {
      return eventsConfigure && addedItems !== undefined
        ? filterDeletedItems(
            removeDuplicates(
              addedItems,
              eventsConfigure[key]?.entities ?? [],
              []
            ),
            addedItems
          )
        : addedItems;
    };

    const addedLabVal = updateValues(soaAddedLabs, LABS);
    const addedExamVal = updateValues(
      soaAddedExamination,
      PHYSICAL_EXAMINATION
    );
    const addedQuestionnaireVal = updateValues(
      soaAddedQuestionnaires,
      QUESTIONNAIRES
    );
    const addedProcedureVal = updateValues(soaAddedProcedures, PROCEDURES);
    const addedStudyProcedureVal = updateValues(
      soaAddedStudyProcedures,
      STUDY_PROCEDURES
    );

    const tmpVisitTypes = scenario?.visit_type;

    setWhetherDraggable({
      [LABS]: addedLabVal[0]?.activity_id,
      [PHYSICAL_EXAMINATION]: addedExamVal[0]?.activity_id,
      [PROCEDURES]: addedProcedureVal[0]?.activity_id,
      [QUESTIONNAIRES]: addedQuestionnaireVal[0]?.activity_id,
      [STUDY_PROCEDURES]: addedStudyProcedureVal[0]?.activity_id,
    });

    setPeriodData(tmpPeriodData);
    setVisitTypes(tmpVisitTypes);
    setLabs(addedLabVal);
    setExamination(addedExamVal);
    setProcedures(addedProcedureVal);
    setQuestionnaire(addedQuestionnaireVal);
    setStudyProcedures(addedStudyProcedureVal);
    setModalityOptions(getModalityOptions(scenario?.["modality_option"]));
    setVisits(visitDetails);    
    setWeeks(WeeksData);   
    setDays(DaysData) ;
    setWeekVisitWindow(weekVisitWindow)
    setDayVisitWindow(dayVisitWindow)

    dispatch(
      updateDPSoaActivities({
        soaPeriod_data: tmpPeriodData,
        soaAddedLabs: addedLabVal,
        soaAddedExamination: addedExamVal,
        soaAddedProcedures: addedProcedureVal,
        soaAddedQuestionnaires: addedQuestionnaireVal,
        soaAddedStudyProcedures: addedStudyProcedureVal,
        soaVisits: visitDetails,
        soaWeeks: WeeksData,
        soaDays: DaysData,
        soaWeekWindow: weekVisitWindow,
        soaDayWindow: dayVisitWindow
      })
    );
  }, [dpSoaState.scenarios]);

  useEffect(() => {
    const data =
      periodData?.map((item, index) => {
        item.period_id = item.period_id === null ? index + 1 : item.period_id;
        item.period_order_index =
          item.period_order_index === null
            ? index + 1
            : item.period_order_index;
        item.period_name =
          item.period_name === null ? item.visit_type : item.period_name;
        return {
          ...item,
        };
      }) || [];
    let weeks_arr = [];
    let days_arr = [];

    const visitArray = [];
    data.forEach((item) => {
      visitArray.push(...item.visits);
      weeks_arr.push(...item.weeks);
      days_arr.push(...item.days);
    });
    setVisits((prevState) => {
      return visitArray;
    });
    setWeeks(weeks_arr);
    setDays(days_arr);
  }, [periodData]);


  const filterData = useCallback((data, searchVal) => {
    return data.filter((item) =>
      item["Standard Activity"].toLowerCase().includes(searchVal.toLowerCase())
    );
  }, []);

  const onTextChange = (e) => {
    const searchVal = e.target.value;
    let lab_result = filterData(labs, searchVal);
    let examination_result = filterData(examination, searchVal);
    let procedure_result = filterData(procedures, searchVal);
    let questionnaire_result = filterData(questionnaire, searchVal);
    let study_procedures_result = filterData(studyProcedures, searchVal);

    setPanelMapping([
      { key: "1", type: LABS, data: lab_result || [] },
      { key: "2", type: PHYSICAL_EXAMINATION, data: examination_result || [] },
      { key: "3", type: PROCEDURES, data: procedure_result || [] },
      { key: "4", type: QUESTIONNAIRES, data: questionnaire_result || [] },
      { key: "5", type: STUDY_PROCEDURES, data: study_procedures_result || [] },
    ]);
    setSearchTxt(searchVal);
  };

  const moveActvity = (idx, category) => {
    setWhetherDraggable((prevState) => ({
      ...prevState,
      [category]: idx,
    }));
  };

  const clickHandler = (e, evt, idx) => {
    e.stopPropagation();
    const { Categories, condition } = evt;
    let tmpCon = condition.slice(0);
    tmpCon[idx] = {
      ...tmpCon[idx],
      modality: '8',
    };
    updateAllCategories(condition, Categories, evt, tmpCon);
  };

  const findConditions = (type, active, over) => {
    const dataMap = {
      Labs: labs,
      "Physical Examination": examination,
      Procedures: procedures,
      Questionnaires: questionnaire,
      "Study Procedures": studyProcedures,
    };

    const dataArray = dataMap[type];
    if (!dataArray) {
      return { activeIndex: -1, overIndex: -1 };
    }

    const activeIndex = dataArray.findIndex((i) => i.activity_id === active.id);
    const overIndex = dataArray.findIndex((i) => i.activity_id === over?.id);

    return { activeIndex, overIndex };
  };

  const onSOADragEnd = (type, { active, over }) => {
    if (active.id !== over?.id) {
      const updateActivities = (setFunction, dispatchKey) => {
        let newActivities;
        setFunction((prevState) => {
          const { activeIndex, overIndex } = findConditions(type, active, over);
          newActivities = arrayMove(prevState, activeIndex, overIndex);
          newActivities = newActivities?.map((item, idx) => ({
            ...item,
            activity_order_index: idx + 1,
          }));
          return newActivities;
        });
        dispatch(updateDPSoaActivities({ [dispatchKey]: newActivities }));
      };

      switch (type) {
        case LABS:
          updateActivities(setLabs, "soaAddedLabs");
          break;
        case PHYSICAL_EXAMINATION:
          updateActivities(setExamination, "soaAddedExamination");
          break;
        case PROCEDURES:
          updateActivities(setProcedures, "soaAddedProcedures");
          break;
        case QUESTIONNAIRES:
          updateActivities(setQuestionnaire, "soaAddedQuestionnaires");
          break;
        case STUDY_PROCEDURES:
          updateActivities(setStudyProcedures, "soaAddedStudyProcedures");
          break;
        default:
          break;
      }
    }
    setDragIndex({
      active: -1,
      over: -1,
      direction: "",
    });
  };

  const onSOADragOver = (customParam, { active, over }) => {
    const { activeIndex, overIndex } = findConditions(
      customParam,
      active,
      over
    );
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? "right" : "left",
    });
  };

  const onModalityChange = (e, evt, idx) => {
    const { Categories, condition } = evt;
    let tmpCon = condition.slice(0);
    tmpCon[idx] = {
      ...tmpCon[idx],
      modality: String(e.target.value),
      modality_name: modalityOptions.find(k => k.key === e.target.value)?.name
    };    
    updateAllCategories(condition, Categories, evt, tmpCon);
  };

  const onFrequencyChange = (value, idx, activityItem) => {
    const { Categories, condition } = activityItem;
    let tmpCon = condition.slice(0);
    if(tmpCon[idx]?.modality_frequency === value) return
    tmpCon[idx] = {
      ...tmpCon[idx],
      modality_frequency: value,
    };
    updateAllCategories(condition, Categories, activityItem, tmpCon);
  }

  const visitMappingHandler = (selectedActivity) => {
    setVisitToBeMapped(selectedActivity);
    setShowVisitMappingModal((v) => !v);
  };

  const finalizeVisitsMapping = (activities) => {
    let newArr = mapConditions(activities, visitTobeMapped);
    let newArray = [...newArr];
    newArray = updateActivities(
      labs,
      newArray,
      setLabs,
      updateDPSoaActivities,
      "soaAddedLabs",
      dispatch,
      modalityOptions
    );
    newArray = updateActivities(
      procedures,
      newArray,
      setProcedures,
      updateDPSoaActivities,
      "soaAddedProcedures",
      dispatch,
      modalityOptions
    );
    newArray = updateActivities(
      examination,
      newArray,
      setExamination,
      updateDPSoaActivities,
      "soaAddedExamination",
      dispatch,
      modalityOptions
    );
    newArray = updateActivities(
      questionnaire,
      newArray,
      setQuestionnaire,
      updateDPSoaActivities,
      "soaAddedQuestionnaires",
      dispatch,
      modalityOptions
    );
    updateActivities(
      studyProcedures,
      newArray,
      setStudyProcedures,
      updateDPSoaActivities,
      "soaAddedStudyProcedures",
      dispatch,
      modalityOptions
    );
  };

  const doCancelVisitMapping = () => {
    setShowVisitMappingModal(false);
  };
  const doConfirmVisitMapping = () => {
    setShowVisitMappingModal(false);
    finalizeVisitsMapping(activitiesMapped);
  };

  const selectedVisitMappingHandler = (obj) => {
    setActivitiesMapped(obj);
  };

  const selectedPeriodMappingHandler = (obj) => {
    setPeriodsMapped(obj)
  }

  const deleteActivityHandler = (item) => {
    const updateStateAndDispatch = (categoryState, updateAction, setState) => {
      const updatedItems = categoryState.filter(
        (d) => item["Standard Activity"] !== d["Standard Activity"]
      );
      setState(updatedItems);
      dispatch(updateDPSoaActivities({ [updateAction]: updatedItems }));
    };

    switch (item.Categories.trim()) {
      case LABS:
        updateStateAndDispatch(
          dpSoaState?.soaAddedLabs,
          "soaAddedLabs",
          setLabs
        );
        break;
      case PHYSICAL_EXAMINATION:
        updateStateAndDispatch(
          dpSoaState?.soaAddedExamination,
          "soaAddedExamination",
          setExamination
        );
        break;
      case PROCEDURES:
        updateStateAndDispatch(
          dpSoaState?.soaAddedProcedures,
          "soaAddedProcedures",
          setProcedures
        );
        break;
      case QUESTIONNAIRES:
        updateStateAndDispatch(
          dpSoaState?.soaAddedQuestionnaires,
          "soaAddedQuestionnaires",
          setQuestionnaire
        );
        break;
      case STUDY_PROCEDURES:
        updateStateAndDispatch(
          dpSoaState?.soaAddedStudyProcedures,
          "soaAddedStudyProcedures",
          setStudyProcedures
        );
        break;
      default:
        break;
    }
  };

  const generatePanel = (panelData) => {
    const { key, type, data } = panelData;    
    const name = `${type} (${data?.length})`;
    const category_type = type;

    return (
      <Panel
        forceRender={true}
        header={
          <div className={styles.eventPanelHead}>
            <div className="event-title e-row">
              <div>
                <span style={{ width: "186px", display: "inline-block" }}>
                  {name}
                </span>
              </div>
            </div>
            <div className="event-title-right"></div>
          </div>
        }
        key={key}
      >
        <div className="shedule-of-event-panel-body">
          <DndContext
            sensors={isLocked ? noSensors : sensors}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={(event) => onSOADragEnd(category_type, event)}
            onDragOver={(event) => onSOADragOver(category_type, event)}
            collisionDetection={closestCenter}
          >
            <SortableContext
              items={data.map((i) => i?.activity_id?.toString())}
              strategy={verticalListSortingStrategy}
            >
              <DragIndexContext.Provider value={dragIndex}>
                {data?.map((evt, idx) => (
                  <DraggableItem
                    key={evt.activity_id}
                    activity_item={evt}
                    type={type}
                    idx={idx}
                    onModalityChange={onModalityChange}
                    clickHandler={clickHandler}
                    moveActivityHandler={moveActvity}
                    visits={visits}
                    modalityOptions={modalityOptions}
                    whetherDraggable={whetherDraggable}
                    unitIndex={unitIndex}
                    onVisitMapping={visitMappingHandler}
                    trial_id={locationState.trial_id}
                    table_id={locationState.tableItems.table_id}
                    dprot_id={locationState.tableItems.dprot_id}
                    onDeleteActivity={deleteActivityHandler}
                    onFrequencyChange={onFrequencyChange}
                    week={weeks[idx] || null}
                    day={days[idx] || null}
                    isReadOnly={isLocked}
                  />
                ))}
              </DragIndexContext.Provider>
            </SortableContext>

            <DragOverlay>
              <div
                style={{
                  fontSize: "10px",
                }}
              >
                {
                  data[
                    data.findIndex((i) => i.activity_id === dragIndex.active)
                  ]?.["Standard Activity"]
                }
              </div>
            </DragOverlay>
          </DndContext>
        </div>
      </Panel>
    );
  };

  const updateAllCategories = (condition, Categories, evt, tmpCon) => {
    const totalVisitTmp = condition.filter(
      (c) => c.modality && c.modality !== ""
    ).length;

    const updateCategory = (items, setFunction, dispatchKey) => {
      const newItems = items.slice(0);
      const targetItem = newItems.find(
        (ele) => ele["Standard Activity"] === evt["Standard Activity"]
      );
      if (targetItem) {
        targetItem.condition = tmpCon;
        targetItem.totalVisit = totalVisitTmp;
        setFunction(newItems);
        dispatch(updateDPSoaActivities({ [dispatchKey]: newItems }));
      }
    };

    switch (Categories) {
      case LABS:
        updateCategory(labs, setLabs, "soaAddedLabs");
        break;
      case PHYSICAL_EXAMINATION:
        updateCategory(examination, setExamination, "soaAddedExamination");
        break;
      case PROCEDURES:
        updateCategory(procedures, setProcedures, "soaAddedProcedures");
        break;
      case QUESTIONNAIRES:
        updateCategory(
          questionnaire,
          setQuestionnaire,
          "soaAddedQuestionnaires"
        );
        break;
      case STUDY_PROCEDURES:
        updateCategory(
          studyProcedures,
          setStudyProcedures,
          "soaAddedStudyProcedures"
        );
        break;
      default:
        break;
    }
  };

  function callback(key) {
    setExpandKeys(key);
  }
  const addVisits = (idx, whetherCopy) => {  
    const overIndex = idx + 1;  
    const newPeriodType = JSON.parse(JSON.stringify(periodData));  
    const targetIndex = periodData.findIndex(item => item.visits.includes(overIndex));  
  
    if (targetIndex !== -1) {  
        newPeriodType[targetIndex].no_of_visits += 1;  
    }  
  
    const visitNew = Math.max(...visits) + 1;  
    const newVisits = [...visits];  
    newVisits.splice(overIndex, 0, visitNew);  
  
    const newWeeks = addItem(weeks, overIndex, whetherCopy, idx);  
    const newDays = addItem(days, overIndex, whetherCopy, idx);  
  
    const newDaysWindow = addWindowItem(dayVisitWindow, overIndex, whetherCopy, idx);  
    const newWeeksWindow = addWindowItem(weekVisitWindow, overIndex, whetherCopy, idx);  

    setWeeks(newWeeks);  
    setDays(newDays);  
    setWeekVisitWindow(newWeeksWindow);  
    setDayVisitWindow(newDaysWindow);  
  
    let _newVisits = [...newVisits];  
    let _newWeeks = [...newWeeks];  
    let _newDays = [...newDays];  
    let _newDayWindow = [...newDaysWindow];  
    let _newWeekWindow = [...newWeeksWindow];  
  
    newPeriodType.forEach((period) => {  
        period.visits = _newVisits.splice(0, period.no_of_visits);  
        period.weeks = _newWeeks.splice(0, period.no_of_visits);  
        period.days = _newDays.splice(0, period.no_of_visits);  
        period.day_visit_window = _newDayWindow.splice(0, period.no_of_visits);  
        period.week_visit_window = _newWeekWindow.splice(0, period.no_of_visits);  
    });  
  
    setPeriodData(newPeriodType);  
    incrementVisit(newPeriodType);  
  
    const setterParams = {  
        func: insertConditionByVisit,  
        args: [idx, whetherCopy],  
        renewFunc: renewCondition,  
        renewArgs: [  
            newVisits.map((_, i) => i + 1),  
            newWeeks.map((_, i) => i + 1),  
            newDays.map((_, i) => i + 1),  
        ],  
    };  
    conditionUpdate(setterParams);  

    const tmpvisits = Array.from({ length: newVisits.length }, (_, idx) => idx + 1);  
    setVisits(tmpvisits);  
    setWeeks(newWeeks);  
    setDays(newDays);  
  
    dispatch(  
        updateDPSoaActivities({  
            soaVisits: tmpvisits,  
            soaPeriod_data: newPeriodType,  
            soaWeeks: newWeeks,  
            soaDays: newDays,  
            soaWeekWindow: newWeeksWindow,  
            soaDayWindow: newDaysWindow,  
        })  
    );  
    // move all the footnotes on the right of current visit to move 1 step right
    moveFootnoteByVisitChange("right", whetherCopy ? "duplicate" : "add", overIndex);
};  

  const deleteVisits = (idx) => {
    let overIndex = idx + 1;
    let newPeriodType = JSON.parse(JSON.stringify(periodData));
    let targetIndex = periodData.findIndex((item) =>
      item.visits.includes(overIndex)
    );

    if (targetIndex !== -1 && newPeriodType[targetIndex].no_of_visits === 1) {
      message.error(
        "There is only one visit under this period. You cannot delete it."
      );
      return;
    } else {
      newPeriodType[targetIndex].no_of_visits -= 1;

      let newVisits = visits.slice();
      newVisits.splice(idx, 1);

      let newWeeks = weeks.slice();      
      newWeeks.splice(idx, 1);

      let newDays = days.slice();      
      newDays.splice(idx, 1);  
      
      let newDaysWindows = dayVisitWindow.slice();      
      newDaysWindows.splice(idx, 1);  

      let newWeeksWindows = weekVisitWindow.slice();      
      newWeeksWindows.splice(idx, 1);  

      setWeeks(newWeeks);
      setDays(newDays);
      setWeekVisitWindow(newWeeksWindows)
      setDayVisitWindow(newDaysWindows)

      let _newVisits = [...newVisits];
      let _newWeeks = [...newWeeks];
      let _newDays = [...newDays];
      let _newDaysWindows = [...newDaysWindows];
      let _newWeeksWindows = [...newWeeksWindows];
      newPeriodType.forEach((period, index) => {
        period.visits = _newVisits.splice(0, period.no_of_visits);
        period.weeks = _newWeeks.splice(0, period.no_of_visits);
        period.days = _newDays.splice(0, period.no_of_visits);
        period.day_visit_window = _newWeeksWindows.splice(0, period.no_of_visits);
        period.week_visit_window = _newDaysWindows.splice(0, period.no_of_visits);
      });

      newPeriodType = incrementVisit(newPeriodType);
      setPeriodData(newPeriodType);

      const setterParams = {
        func: deleteConditionByVisit,
        args: [idx],
        renewFunc: renewCondition,
        renewArgs: [newVisits.map((_, i) => i + 1),
          _newWeeks.map((_, i) => i + 1),
          _newDays.map((_, i) => i + 1),]
      }
      conditionUpdate(setterParams)

      const tmpvisits = Array.from({ length: newVisits.length }, (_, idx) => idx + 1);  
      setVisits(tmpvisits);  

      dispatch(
        updateDPSoaActivities({
          soaVisits: tmpvisits,
          soaWeeks: newWeeks,
          soaDays: newDays,
          soaPeriod_data: newPeriodType,
          soaWeekWindow: newWeeksWindows,  
          soaDayWindow: newDaysWindows,  
        })
      );
      // move all the footnotes on the right of current visit to move 1 step left
      moveFootnoteByVisitChange("left", "delete", overIndex);
    }
  };

  const resetVisit = (idx) => {
    const setterParams = {
      func: resetConditionVisit,
      args: [idx+1],
      renewFunc: null,
      renewArgs: null
    }
    conditionUpdate(setterParams)
  }

  const actionHandler = (name, idx) => {
    switch (name) {
      case "Add Visit":
        addVisits(idx, false);
        break;
      case "Delete Visit":
        deleteVisits(idx);
        break;
      case "Duplicate Visit":
        addVisits(idx, true);
        break;
      case "Add Footnote":
        // get period id by visit number
        const period = periodData.find(periodItem => periodItem.visits.includes(visits[idx]))
        setIsOpenAddFootnoteModal({
          visible: true,
          footnote_group: FOOTNOTE_GROUP.VISIT,
          trial_uuid: locationState.trial_id,
          table_id: locationState.tableItems.table_id,
          dprot_id: locationState.tableItems.dprot_id,
          visit_number: visits[idx],
          period_id: period?.period_id
        })
        break;
      case "Move Visit":
        const overIndex = idx + 1;
        const newPeriodType = JSON.parse(JSON.stringify(periodData));
        const targetIndex = periodData.findIndex((item) =>
          item.visits.includes(overIndex)
        );
        if (targetIndex !== -1 && newPeriodType[targetIndex].no_of_visits === 1) {
          message.error(
            "There is only one visit under this period. You cannot move it."
          );
          return;
        }
        setWhetherVisitDraggable(idx+1)
        break;
      case "Reset Visit":
        resetVisit(idx)
        break
      default:
        break;
    }
  };

  const handleCancelWindow = () => setVisitWindowOpen({
    visible: false,
    visit: -1
  });

  const handleSubmitWindow = (visit_window_val, visit) => {           
    // Find the period containing the visit  
    const period = periodData.find(period => period.visits.includes(visit));  
    
    if (!period) {  
      console.error("Visit not found in any period:", visit);  
      return;  
    }  
    
    // Find the index of the visit within the found period  
    const vIdx = period.visits.findIndex(v => v === visit);  
    
    if (vIdx === -1) {  
      console.error("Visit not found in the period:", visit);  
      return;  
    }  
    
    // Extract the period ID  
    const period_id = period.period_id;      
    
    const conversionFactor = windowUnit === 'Week' ? 7 : 1 / 7;  
    const visitWindow = convertVisitWindow(visit_window_val, conversionFactor);  
    
    // Create a deep copy of the specific period to avoid mutating state directly  
    const updatedPeriod = JSON.parse(JSON.stringify(period));  
    
    if (windowUnit === 'Week') {  
      updatedPeriod.week_visit_window[vIdx] = visit_window_val;  
      updatedPeriod.day_visit_window[vIdx] = visitWindow;  
    } else if (windowUnit === 'Day') {  
      updatedPeriod.day_visit_window[vIdx] = visit_window_val;  
      updatedPeriod.week_visit_window[vIdx] = visitWindow;  
    }  
    
    // Create a new periodData array with the updated period  
    const updatedPeriodData = periodData.map(p =>   
      p.period_id === period_id ? updatedPeriod : p  
    );  
        
    setPeriodData(updatedPeriodData);  
    
    const weekVisitWindow = weekDayWindowMapping(updatedPeriodData ?? {}, 'week');  
    const dayVisitWindow = weekDayWindowMapping(updatedPeriodData ?? {}, 'day');  
    setWeekVisitWindow(weekVisitWindow);  
    setDayVisitWindow(dayVisitWindow);  
    
    dispatch(  
      updateDPSoaActivities({  
        soaPeriod_data: updatedPeriodData,  
        soaWeekWindow: weekVisitWindow,  
        soaDayWindow: dayVisitWindow  
      })  
    );  
    
    setVisitWindowOpen({  
      visible: false,  
      visit: -1  
    });  
  };  

  const navToAddActivity = () => {
    history.push({
      pathname: "/dprotocol/soa-details/add-activity-soa",
      state: locationState,
    });
  };

  // Initialize state for storing out-of-order indices  
  const [weeksOutOfOrderIndices, setWeeksOutOfOrderIndices] = useState([]);  
  const [daysOutOfOrderIndices, setDaysOutOfOrderIndices] = useState([]);  

  const onSaveSoa = async () => {
    try {
      setIsSaving(true);      
       // Check if any week value is outside the range of -1000 to 1000  
    if (visitUnit === "Week") {  
      const invalidWeekValue = weeks.some(week => week < -1000 || week > 1000);  
      if (invalidWeekValue) {  
        message.error("The value for Weeks must be between -1000 and 1000");  
        setIsSaving(false);  
        return;  
      }  
    }  
  
    // Check if any day value is outside the range of -7000 to 7000  
    if (visitUnit !== "Week") {  
      const invalidDayValue = days.some(day => day < -7000 || day > 7000);  
      if (invalidDayValue) {  
        message.error("The value for Days must be between -7000 and 7000");  
        setIsSaving(false);  
        return;  
      }  
    }  
    // Trim period names to 200 characters  
    const trimmedPeriodData = periodData.map(period => ({  
      ...period,  
      period_name: period.period_name.substring(0, 500)  
    }));      
    
      // Check for duplicate period names  
       // Extract period names  
      const periodNames = trimmedPeriodData.map(period => period.period_name);  

      // Check for blank period names  
      const hasBlankPeriodName = periodNames.some(name => !name.trim());  
      if (hasBlankPeriodName) {  
          message.error("Study Period name should not be empty.");  
          setIsSaving(false);  
          return;  
      }  

      const duplicates = periodNames.filter((name, index) => periodNames.indexOf(name) !== index);  
      
      if (duplicates.length > 0) {  
          message.error("Study period name should be unique.");  
          setIsSaving(false);  
          return;  
      }
      let weekOutOfOrder = [];  
      let dayOutOfOrder = [];  
      // Check if weeks and days are in ascending order       
      if(visitUnit === "Week"){
        weekOutOfOrder  = findOutOfOrderIndices(weeks);   
        setWeeksOutOfOrderIndices(weekOutOfOrder);           
      }else{
        dayOutOfOrder = findOutOfOrderIndices(days); 
        setDaysOutOfOrderIndices(dayOutOfOrder);
      }             
      
      if (weekOutOfOrder.length > 0 || dayOutOfOrder.length > 0) {  
        message.error("Weeks and Days must be in ascending order.");  
        setIsSaving(false);  
        return;  
      } 

      let _periodType = reOrderVisitsInPeriodType(visits, trimmedPeriodData);      
      const scheduleOfEvents = {
        Labs: {
          entities: addPeriodIDtoCondition(labs, _periodType, weeks, days),
        },
        "Physical Examination": {
          entities: addPeriodIDtoCondition(examination, _periodType, weeks, days),
        },
        Procedures: {
          entities: addPeriodIDtoCondition(procedures, _periodType, weeks, days),
        },
        Questionnaires: {
          entities: addPeriodIDtoCondition(questionnaire, _periodType, weeks, days),
        },
        "Study Procedures": {
          entities: addPeriodIDtoCondition(studyProcedures, _periodType, weeks, days),
        },
        period_data: _periodType,
        visits: visits.map((c, i) => (c = i + 1)),
        Weeks: weeks,
        Days: days,
        WeekWindow: weekVisitWindow,
        DayWindow: dayVisitWindow
      };
      const payload = {
        dprot_id: locationState?.tableItems?.dprot_id,
        user_name: locationState?.user_name,
        trial_uuid: locationState?.trial_id,
        soa: scheduleOfEvents,
        oncology_flag: false,
      };

      const response = await saveSoaActivity(payload);      
      if (response && Object.keys(response).length > 1) {
        // save footnote data
        await doFootnoteHardSave(locationState?.trial_id, locationState?.tableItems.table_id, visitUnit, setIsSaving);
        // refresh SoA Data in redux
        props?.fetchSoAData && props?.fetchSoAData()
        message.success("Save successfully");
        setIsSaving(false);
        setWeeksOutOfOrderIndices([]);
        setDaysOutOfOrderIndices([]);
        setHighlightWeekIndex(-1);         
      } else {
        message.error("Failed to save");
        setIsSaving(false);
        return;
      }
    } catch (err) {
      message.error("Failed to save");
      setIsSaving(false);
    } finally {
      setIsSaving(false);
    }
  };

  const weekChangeHandler = (e, idx) => {  

    const regex = /^-?(?!$)(?!.*--)\d+$/;
    const value = e.target.value;
        
    const val:any = value === '-' ? '-' : (value === '' ? null : (regex.test(value) ? parseInt(value) : null));   
    
    // if(val != '-' && val > 1000) return;
    if (val !== '-' && ((visitUnit === 'Week' && (val > 1000 || val < -1000)) || (visitUnit === 'Day' && (val > 7000 || val < -7000)))) {  
      return;  
    }  

    const updatedWeeks = [...weeks];  
    const updatedDays = [...days];    

    let weeksSelectionIdx = updatedWeeks.findIndex((k, i) => idx == i);
    let daysSelectionIdx = updatedDays.findIndex((k, i) => idx == i);

    // Update logic based on visitUnit  
    if (visitUnit === 'Week') {  
        updatedWeeks[weeksSelectionIdx] = val;  
        updatedDays[daysSelectionIdx] = val !== null ? Math.round(val * 7) : null;  
    } else if (visitUnit === 'Day') {  
        updatedDays[daysSelectionIdx] = val;  
        updatedWeeks[weeksSelectionIdx] = val !== null ? Math.round(val / 7) : null;  
    }  

    setWeeks(updatedWeeks);  
    setDays(updatedDays);  
    setWeeksOutOfOrderIndices([]);
    setDaysOutOfOrderIndices([]);    
    
    // Check for ascending order issues, ignoring null values  
    const outOfOrderIndices = {  
      weeks: checkOrder(updatedWeeks),  
      days: checkOrder(updatedDays)  
    };  
  
    setWeeksOutOfOrderIndices(outOfOrderIndices.weeks);  
    setDaysOutOfOrderIndices(outOfOrderIndices.days); 
    
    let visitIndex = 0;  
    const updatedPeriodData = [...periodData];  

    updatedPeriodData.forEach((period) => {  
        period.weeks = updatedWeeks.slice(visitIndex, visitIndex + period.no_of_visits);  
        period.days = updatedDays.slice(visitIndex, visitIndex + period.no_of_visits);  
        visitIndex += period.no_of_visits;  
    });  

    setPeriodData(updatedPeriodData);  

    dispatch(updateDPSoaActivities({  
        soaWeeks: updatedWeeks,  
        soaDays: updatedDays,  
        soaPeriod_data: updatedPeriodData,  
    }));      
  }; 


  const addDuplicatePeriod = (idx, whetherDuplicate) => {
    const data = JSON.parse(JSON.stringify(periodData))
    const prevPeriod = data.find((i) => i.period_id === idx);  
    const noOfVisits = prevPeriod?.no_of_visits || 0;  
    const maxVisit = Math.max(...visits);  
    const visitArr = Array.from({ length: noOfVisits }, (_, i) => i + maxVisit + 1);  
    
    const initWindowVal = {  
      lower_visit_value: null,  
      upper_visit_value: null,  
      visit_window_type_id: null,  
    };  

    // Determine the weeks and days for the new period  
    const weekArr = Array(noOfVisits).fill(null);  // Placeholder for new weeks  
    const dayArr = Array(noOfVisits).fill(null);   // Placeholder for new days  
    const dayWindowArr = Array(noOfVisits).fill(initWindowVal);
    const weekWindowArr = Array(noOfVisits).fill(initWindowVal);
    const counter = data.filter(k => k.visit_type === prevPeriod.visit_type)?.length || 0 + 1;
    const newPeriod = {  
      period_name: toSentenceCase(prevPeriod?.visit_type) + counter,
      period_id: "",  
      period_order_index: "",  
      no_of_visits: noOfVisits,  
      visit_type_id: prevPeriod?.visit_type_id,  
      visit_type: prevPeriod?.visit_type,  
      visits: visitArr,  
      weeks: whetherDuplicate? prevPeriod.weeks : weekArr,  
      days:  whetherDuplicate? prevPeriod.days : dayArr, // Added days for the new period  
      day_visit_window: whetherDuplicate? prevPeriod.day_visit_window : dayWindowArr,
      week_visit_window: whetherDuplicate? prevPeriod.week_visit_window : weekWindowArr
    };  

    const insertIndex = periodData.findIndex(k => k.period_id === idx)
    data.splice(insertIndex+1, 0, newPeriod);  
    const incrementedArr = incrementVisit(  
      data.map((item, index) => ({  
        ...item,  
        period_order_index: index + 1,  
        period_id: index + 1,  
      }))  
    ); 
    const allVisits = incrementedArr.flatMap(item => item.visits);  
    const newWeeks = incrementedArr.flatMap(week => week.weeks); 
    const newDays = incrementedArr.flatMap(day => day.days); 
    const newDaysWindow = incrementedArr.flatMap(day => day.day_visit_window); 
    const newWeeksWindow = incrementedArr.flatMap(week => week.week_visit_window); 
    return {
      allVisits,
      newWeeks,
      newDays,
      incrementedArr,
      prevPeriod,
      data,
      newDaysWindow,
      newWeeksWindow
    } 
  }

  const addPeriod = (idx, direction, whetherDuplicate) => {  
    const { 
      allVisits,
      newWeeks,
      newDays,
      incrementedArr,
      prevPeriod,
      newDaysWindow,
      newWeeksWindow
    } = addDuplicatePeriod(idx, whetherDuplicate)
    setPeriodData(incrementedArr); 
    const setterParams = {
      func: addConditionByPeriod,
      args: [prevPeriod, incrementedArr, whetherDuplicate, direction],
      renewFunc: renewCondition,
      renewArgs: [allVisits, newWeeks, newDays]
    }
    conditionUpdate(setterParams)

    setWeeks(newWeeks);
    setDays(newDays);
    setWeekVisitWindow(newWeeksWindow)
    setDayVisitWindow(newDaysWindow)

    dispatch(updateDPSoaActivities({  
      soaVisits: allVisits,  
      soaPeriod_data: incrementedArr,  
      soaWeeks: newWeeks,  
      soaDays: newDays,
      soaWeekWindow: newWeeksWindow,  
      soaDayWindow: newDaysWindow,  
    }));  
    // move all the footnotes on the right of current period to move right
    moveFootnoteByPeriodChange(direction, whetherDuplicate ? "duplicate" : "add", idx, prevPeriod, incrementedArr[idx].period_name);
  }; 
  
  const deletePeriod = (idx) => {
    const data = JSON.parse(JSON.stringify(periodData))
    if(data.length == 1) return;
    let removedData = data.filter( k => k.period_id !== idx).map( (item, index)=> (({...item, period_order_index: index+1, period_id: index+1}))) 
    const updatedData =  incrementVisit(removedData)
    setPeriodData(updatedData);
    const _visits = updatedData?.map(item => item?.visits).flat();  
    const newWeeks = updatedData?.map(item=> item.weeks).flat();     
    const newDays = updatedData?.map(item=> item.days).flat(); 
    const newDaysWindows = updatedData?.map(item=> item.day_visit_window).flat(); 
    const newWeeksWindows = updatedData?.map(item=> item.week_visit_window).flat(); 
         
    const prevPeriod = data[data.findIndex((i) => i.period_id === idx)]
    const tempPeriodArray = _visits.map((visit: number) => {
      const periodItem: Digital_Protocol_SoA_Table_Data.PeriodData = updatedData.find((period: Digital_Protocol_SoA_Table_Data.PeriodData) => period.visits.includes(visit));
      return periodItem.period_id;
    });
    const setterParams = {
      func: deleteConditionByPeriod,
      args: [prevPeriod, data],
      renewFunc: renewCondition,
      renewArgs: [_visits, newWeeks, newDays, tempPeriodArray]
    }
    conditionUpdate(setterParams)


    setWeeks(newWeeks);
    setDays(newDays);
    setWeekVisitWindow(newWeeksWindows);  
    setDayVisitWindow(newDaysWindows);  

    dispatch(updateDPSoaActivities({  
      soaVisits: _visits,  
      soaPeriod_data: updatedData,  
      soaWeeks: newWeeks,  
      soaDays: newDays,
      soaWeekWindow: newWeeksWindows,  
      soaDayWindow: newDaysWindows,  
    }));  
    // move all the footnotes on the right of current period to move left
    moveFootnoteByPeriodChange('left', 'delete', idx, prevPeriod);
  }
 
  const resetPeriod = (idx) => {
    const data = JSON.parse(JSON.stringify(periodData))
    const entities = [labs, examination, procedures, questionnaire, studyProcedures
    ];  

    // Find the period to reset  
    const periodIndex = data.findIndex((i) => i.period_order_index === idx);  
    if (periodIndex === -1) return;  
  
    const prevPeriod = data[periodIndex]; 

    // Update the period with reset values  
    data[periodIndex] = {  
      ...prevPeriod, 
    };  
    setPeriodData(data);  

    const setterParams = {
      func: resetConditionByPeriod,
      args: [prevPeriod],
      renewFunc: null,
      renewArgs: null
    }
    conditionUpdate(setterParams)

    // Collect all visits, weeks, and days after reset  
    const allVisits = data.flatMap(item => item.visits || []);    
    const allWeeks = data.flatMap(item => item.weeks || []);    
    const allDays = data.flatMap(item => item.days || []);   

    setWeeks(allWeeks);
    setDays(allDays);

    dispatch(updateDPSoaActivities({  
      soaPeriod_data: data, 
      soaVisits: allVisits,  
      soaWeeks: allWeeks,  
      soaDays: allDays, 
    }));  
  }

  const periodChangeHandler = (name, periodId) => {
    switch(name) {
      case 'Add Period':
        addPeriod(periodId, 'right', false)
        break
      case 'Delete Period':
        deletePeriod(periodId)
        break
      case 'Reset Period':
        resetPeriod(periodId)
        break
      case 'Duplicate Period':
        addPeriod(periodId, 'right', true)
        break
      case 'Move Period':
        setWhetherPeriodDraggable(periodId)
        break
      case 'Add Footnote':
        const period = periodData.find(item => item.period_id === periodId)
        setIsOpenAddFootnoteModal({
          visible: true,
          footnote_group: FOOTNOTE_GROUP.PERIOD,
          trial_uuid: locationState.trial_id,
          table_id: locationState.tableItems.table_id,
          dprot_id: locationState.tableItems.dprot_id,
          period_id: periodId,
          period,
        });
        break
      default:
        break
    }
  }

  const editPeriodFootnoteHandler = (period: Digital_Protocol_SoA_Table_Data.PeriodData, footnote_type_id: number) => {
    setIsOpenAddFootnoteModal({
      visible: true,
      footnote_group: FOOTNOTE_GROUP.PERIOD,
      trial_uuid: locationState.trial_id,
      table_id: locationState.tableItems.table_id,
      dprot_id: locationState.tableItems.dprot_id,
      period_id: period.period_id,
      period,
      footnote_type_id
    });
  }

  const periodNameChangeHandler = (e, periodId, periodName) => { 
    let value = e.target.value.trim(); 
    if (value === periodName) return; // No changes  
    const period_data = [...periodData] 

    // Exclude the current period from duplicate check  
    const periodIndex = period_data.findIndex(  
      period => period.period_id !== periodId && period.period_name === value  
    );  
    
    // let periodIndex = period_data.findIndex(period => period.period_name === value);
    
    if(periodIndex > -1) {      
      message.error(
        "Study period name should be unique."
      );
      return;
    }
    const period_data_updated = period_data.map( item => {
      if(item.period_id == periodId && item.period_name == periodName) {
          item.period_name = value;
      }
      return item;
    });
    setPeriodData(period_data_updated)
    dispatch(updateDPSoaActivities({  
      soaPeriod_data:period_data_updated
    })); 
    updatePreviewByPeriodVisit(periodName, value, FOOTNOTE_GROUP.PERIOD, periodId);
  }

  const [highlightWeekIndex, setHighlightWeekIndex] = useState<number>(-1);

  const onPeriodDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setPeriodData((prevState) => {
        const activeIndex = prevState.findIndex((i) => i.period_id === active?.id);
        const overIndex = prevState.findIndex((i) => i.period_id === over?.id);

        let newVisits = visits?.slice(0)
        newVisits = handleDragData(activeIndex, overIndex, newVisits, prevState, periodData)
        
        const tempVisitArray = newVisits.map((c,i) => c = i + 1)
        newVisits = JSON.parse(JSON.stringify(tempVisitArray));
        let newWeeks = weeks?.slice(0)
        newWeeks = handleDragData(activeIndex, overIndex, newWeeks, prevState, periodData)
        let newDays = days?.slice(0)
        newDays = handleDragData(activeIndex, overIndex, newDays, prevState, periodData)
        let newWeekWindow = weekVisitWindow?.slice()
        newWeekWindow = handleDragData(activeIndex, overIndex, newWeekWindow, prevState, periodData)

        let newDayWindow = dayVisitWindow?.slice()
        newDayWindow = handleDragData(activeIndex, overIndex, newDayWindow, prevState, periodData)

        let newPeriodData = arrayMove(prevState, activeIndex, overIndex)

        setWeeksOutOfOrderIndices([]);
        setDaysOutOfOrderIndices([]);  
        setHighlightWeekIndex(getWeeksOrderIndex(newWeeks));
        setHighlightWeekIndex(getWeeksOrderIndex(newDays));

        newPeriodData = newPeriodData.map((k, idx) => ({...k, period_order_index : idx + 1, period_id: idx + 1, visits: newVisits.splice(0, k.no_of_visits)}))
        const tempPeriodArray = tempVisitArray.map((visit: number) => {
          const periodItem: Digital_Protocol_SoA_Table_Data.PeriodData = newPeriodData.find((period: Digital_Protocol_SoA_Table_Data.PeriodData) => period.visits.includes(visit));
          return periodItem.period_id;
        });
        const setterParams = {
          func: reorderCondition,
          args: [activeIndex, overIndex, prevState, periodData],
          renewFunc: renewCondition,
          renewArgs: [tempVisitArray, newWeeks, newDays, tempPeriodArray]
        }
        conditionUpdate(setterParams)

        setVisits(tempVisitArray);   
        setWeekVisitWindow(newWeekWindow);  
        setDayVisitWindow(newDayWindow);  
        dispatch(updateDPSoaActivities({  
          soaVisits: tempVisitArray,  
          soaPeriod_data: newPeriodData,  
          soaWeeks: newWeeks,  
          soaDays: newDays, 
          soaWeekWindow: newWeekWindow,  
          soaDayWindow: newDayWindow,  
        })); 
        return newPeriodData;
      });
      // move all the footnotes between the start position and the end position
      moveFootnoteByPeriodMovement(over.id > active.id ? "right" : "left", active.id, over.id, periodData);
    }
    setDragPeriodIndex({
      active: -1,
      over: -1,
      direction:'',
    });
  }  

  const conditionUpdate = ({ func, args, renewFunc, renewArgs }) => {
    const entities = [
      labs, 
      examination, 
      procedures, 
      questionnaire, 
      studyProcedures
    ];  
    const updatedEntities = entities.map(entity => {  
      const temp = func(entity, ...args);
      return renewFunc ? renewFunc(temp, ...renewArgs) : temp;  
    });  
    [
      setLabs, 
      setExamination, 
      setProcedures, 
      setQuestionnaire, 
      setStudyProcedures
    ].forEach((setter, i) => {  
      setter(updatedEntities[i]);  
    }); 
    dispatch(updateDPSoaActivities({  
      soaAddedLabs: updatedEntities[0],  
      soaAddedExamination: updatedEntities[1],  
      soaAddedProcedures: updatedEntities[2],  
      soaAddedQuestionnaires: updatedEntities[3],  
      soaAddedStudyProcedures: updatedEntities[4],  
    })); 
  }
  
  const onVisitDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setVisits((prevState) => { 
        let newPeriodData = JSON.parse(JSON.stringify(periodData))
        const findPeriodDetails = (visitId) => {  
          const index = newPeriodData.findIndex(period => period.visits.includes(visitId));  
          return { index, period: newPeriodData[index] };  
        };  

        const { index: p_idx, period: activePeriod } = findPeriodDetails(active.id);  
        const { index: o_idx, period: overPeriod } = findPeriodDetails(over.id);  
  
        const activeIndex = visits.findIndex((i) => i === active?.id);
        const overIndex = visits.findIndex((i) => i === over?.id);
  
        if(activePeriod?.no_of_visits === 1) {
          return prevState
        }
        if(activePeriod?.period_id == overPeriod?.period_id) {
          newPeriodData[p_idx].no_of_visits = activePeriod?.no_of_visits
        } else {
          newPeriodData[p_idx].no_of_visits = newPeriodData[p_idx].no_of_visits - 1
          newPeriodData[o_idx].no_of_visits = newPeriodData[o_idx].no_of_visits + 1
        }
         
        const moveItems = (items) => arrayMove([...items], activeIndex, overIndex); 
        let newVisits = prevState.slice(0)    
        let newWeeks = weeks.slice(0)
        let newDays = days.slice(0)

        let newWeekWindow = weekVisitWindow.slice(0)
        let newDayWindow = dayVisitWindow.slice(0)
        newWeekWindow = moveItems(weekVisitWindow)
        newDayWindow =  moveItems(dayVisitWindow)

        newVisits = moveItems(prevState);  
        newWeeks = moveItems(weeks);  
        newDays = moveItems(days);  
  
        let _newVisits = JSON.parse(JSON.stringify(newVisits))?.map((c,i) => c = i + 1)
        let _newWeeks = JSON.parse(JSON.stringify(newWeeks))
        let _newDays = JSON.parse(JSON.stringify(newDays))
        let _newWeekWindow = JSON.parse(JSON.stringify(newWeekWindow))
        let _newDayWindow = JSON.parse(JSON.stringify(newDayWindow))
        setWeeksOutOfOrderIndices([]);
        setDaysOutOfOrderIndices([]);  
        setHighlightWeekIndex(getWeeksOrderIndex(_newWeeks))

        setWeekVisitWindow(newWeekWindow);  
        setDayVisitWindow(newDayWindow);  

        newPeriodData.forEach((period, index) => {
          const visits = _newVisits.splice(0, period.no_of_visits)
          const weeks = _newWeeks.splice(0, period.no_of_visits)            
          const days = _newDays.splice(0, period.no_of_visits)
          const day_visit_window = _newDayWindow.splice(0, period.no_of_visits);  
          const week_visit_window = _newWeekWindow.splice(0, period.no_of_visits);  
          period.visits = visits 
          period.days = weeks
          period.weeks = weeks
          period.days = days
          period.day_visit_window = day_visit_window
          period.week_visit_window = week_visit_window
        })   
  
        setPeriodData(newPeriodData)
        const tempVisitArray = newVisits.map((c,i) => c = i + 1)
        const tempPeriodArray = tempVisitArray.map((visit: number) => {
          const periodItem: Digital_Protocol_SoA_Table_Data.PeriodData = newPeriodData.find((period: Digital_Protocol_SoA_Table_Data.PeriodData) => period.visits.includes(visit))
          return periodItem.period_id
        })
        const setterParams = {
          func: reorderConditionByVisit,
          args: [activeIndex, overIndex],
          renewFunc: renewCondition,
          renewArgs: [tempVisitArray, newWeeks, newDays, tempPeriodArray]
        }
        conditionUpdate(setterParams)

        dispatch(updateDPSoaActivities({  
          soaVisits: tempVisitArray,  
          soaPeriod_data: newPeriodData,  
          soaWeeks: newWeeks,  
          soaDays: newDays,
          soaWeekWindow: newWeekWindow,  
          soaDayWindow: newDayWindow,  
        })); 
        return tempVisitArray
      });
      // move all the footnotes between the start position and the end position
      moveFootnoteByVisitMovement(over.id > active.id ? "right" : "left", active.id, over.id, periodData);
    }

    setDragVisitIndex({
      active: -1,
      over: -1,
      direction:'',
    });
    setWhetherVisitDraggable(0)
  }

  const onVisitDragOver = ({ active, over }) => {
    const activeIndex = visits.findIndex((i) => i === active.id);
    const overIndex = visits.findIndex((i) => i === over?.id);
    setDragVisitIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? 'right' : 'left',
    });
  }
  
  const onPeriodDragOver = ({ active, over }) => {
    const activeIndex = periodData.findIndex((i) => i.period_id === active.id);
      const overIndex = periodData.findIndex((i) => i.period_id === over?.id);
      setHighlightWeekIndex(-1)
      setDragIndex({
        active: active.id,
        over: over?.id,
        direction: overIndex > activeIndex ? 'right' : 'left',
      });

    return periodData
  }
  
  const onVisitTypeChange = (evt, indx) => {
    const data = [...periodData]
    const value = evt.target.value;
    const visitsDetail = visitTypes.find(k => k.visit_type == value)
    const visit_type_updated = data.map( item => {
      if(item.period_id == indx) {
          item.visit_type = value
          item.visit_type_id = visitsDetail.visit_type_id
      }
      return item
  })
    setPeriodData(visit_type_updated)
    dispatch(updateDPSoaActivities({  
      soaPeriod_data: visit_type_updated
    })); 
  }

  const handleEditInFootnoteTooltip = (footnote_type_id, otherParams) => {
    // get period id by visit number
    const period = periodData.find(periodItem => periodItem.visits.includes(otherParams?.visitNumber))
    setIsOpenAddFootnoteModal({
        visible: true,
        footnote_group: FOOTNOTE_GROUP.VISIT,
        trial_uuid: locationState.trial_id,
        table_id: locationState.tableItems.table_id,
        dprot_id: locationState.tableItems.dprot_id,
        visit_number: otherParams?.visitNumber,
        period_id: period?.period_id,
        footnote_type_id
    })
  }

  const handleDeleteFootnote = (footnote_id: any[]) => {
    // soft delete footnote
    doFootnoteSoftDelete(footnote_id);
  }

  const visitWindowHandler = (visit) => {
    setVisitWindowOpen({
      visible: true,
      visit
    })
  }

  return (
    <>
      <div className={styles.headingContainer}>
        <span className={styles.heading}>Schedule of activities</span>
      </div>

      {existFootnoteWarning && (
        <div className={styles.errorMsg}>Your recent changes have impacted some footnotes at the activity or cell level. Please review and update them accordingly.</div>
      )}

      <div
        className={styles.eventDashboard}
        style={{
          minHeight: "500px",
          maxHeight: "700px",
          overflowY: "scroll",
          overflowX: "scroll",
        }}
      >
        <div
          className={styles.dashboardHead}
          style={{
            top: "0px",
            position: "sticky",
            zIndex: 100,
            backgroundColor: "#FFF",
          }}
        >
          <div className={styles.eventListHead} ref={eventListHead}>
            <div className={`${styles.headRow} ${styles.relative}`}>
              <div
                className={`${styles.leftPanelHeadRow} ${styles.centerBorderless}`}
              >
                <div
                  className={`${styles.visitRow} ${styles.eRow} ${styles.middlePanel}`}
                >
                  <div className={`${styles.colunm}`}></div>
                </div>
                <div className={styles.leftPanelContainer}>
                  <div className={styles.legendWrapper}>
                    <MultiSwitch
                      options={["Activity visit map", "Frequency view"]}
                      value={unitIndex}
                      onChange={(i) => setUnitIndex(i.toString())}
                    />
                  </div>
                </div>
              </div>
              <div
                className={`${styles.visitRow} ${styles.eRow} ${styles.addActivityBtn}`}
                style={{ height: "50px" }}
              >
                {!isLocked && (
                  <div className={styles.actionContainer}>
                    <Button
                      type="primary"
                      className={styles.saveBtn}
                      disabled={disabledSave}
                      onClick={onSaveSoa}
                    >
                      Save
                    </Button>

                    <Button
                      type="primary"
                      className={`${styles.saveBtn} ${styles.activityBtn}`}
                      onClick={navToAddActivity}
                    >
                      <PlusOutlined /> Add activity
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className={`${styles.headRow} ${styles.relative}`}>
              <div className={styles.leftPanelHeadRow}>
                <div
                  className={`${styles.visitRow} ${styles.eRow} ${styles.middlePanel}`}
                >
                  <div className={`${styles.colunm} ${styles.td}`}></div>
                </div>
                <div className={styles.leftPanelContainer}>
                  <div
                    className={`${styles.legendWrapper} ${styles.searchBox}`}
                  >
                    <Input
                      id="icon"
                      placeholder="Search"
                      prefix={<SearchOutlined />}
                      onChange={onTextChange}
                      value={searchTxt}
                    />
                  </div>
                </div>
              </div>
              <div
                className={`${styles.visitRow} ${styles.eRow}`}
                style={{ height: "50px" }}
              >
                <DndContext
                  sensors={isLocked ? noSensors : sensors}
                  modifiers={[restrictToHorizontalAxis]}
                  onDragEnd={onPeriodDragEnd}
                  onDragOver={onPeriodDragOver}
                  collisionDetection={closestCenter}
                >
                  <SortableContext items={periodData.map((i) => i?.period_id?.toString())} strategy={horizontalListSortingStrategy}>
                  <DragIndexContext.Provider value={dragPeriodIndex}> 
                  {periodData?.map((period, index) => (
                      <DraggablePeriod 
                        periodItem={period} 
                        idx={index}
                        key={period.period_id} 
                        whetherPeriodDraggable={whetherPeriodDraggable}
                        periodNameChangeEvt={periodNameChangeHandler}
                        onAction={periodChangeHandler} 
                        visitType={visitTypes}
                        isReadOnly={isLocked}
                        visitChangeHandler={onVisitTypeChange}
                        editPeriodFootnoteHandler={editPeriodFootnoteHandler}
                      />
                    ))}
                  </DragIndexContext.Provider> 
                  </SortableContext>
                  <DragOverlay>
                    <div
                      style={{
                        fontSize: '10px', 
                      }}
                    >
                    {periodData[periodData.findIndex((i) => i.period_id === dragIndex.active)]?.period_name}
                    </div>
                  </DragOverlay>
                </DndContext>
              </div>
            </div>

            {["Visit number", visitUnit === "Day" ? "Days" : "Weeks", "Visit window"].map((title, index) => (
              <div
                className={`${styles.headRow} ${styles.relative}`}
                key={index}
              >
                <div className={styles.leftPanelHeadRow}>
                  <div
                    className={`${styles.visitRow} ${styles.eRow} ${styles.middlePanel}`}
                  >
                    <div className={`${styles.colunm} ${styles.td}`}>
                      {title}
                    </div>
                  </div>
                  <div className={styles.leftPanelContainer}>
                    <div className={styles.legendWrapper}>
                      {title === "Visit window" && (
                        <>
                          <div
                            className={`${styles.legendHeaders} ${styles.activityHeader}`}
                          >
                            {" "}
                            My activities
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className={`${styles.visitRow} ${styles.eRow} ${styles.centerAlign}`}
                  style={{ height: "50px" }}
                >
                  {title === "Visit number" && (
                    <DndContext
                      sensors={isLocked ? noSensors : sensors}
                      modifiers={[restrictToHorizontalAxis]}
                      onDragEnd={onVisitDragEnd}
                      onDragOver={onVisitDragOver}
                      collisionDetection={closestCenter}
                    >
                      <SortableContext items={visits?.map((i) => i?.toString())} strategy={horizontalListSortingStrategy}>
                        <DragIndexContext.Provider value={dragVisitIndex}> 
                          {visits?.map((item,i) => 
                             ( 
                              <div className={`${styles.numCell} ${styles.td}`} key={i}>
                                <DraggableVisit 
                                whetherVisitDraggable={whetherVisitDraggable}
                                item={item}
                                key={`${title}-${item}`} 
                                onAction={actionHandler} 
                                handleEditInFootnoteTooltip={handleEditInFootnoteTooltip}
                                handleDeleteFootnote={handleDeleteFootnote}
                                idx={i}
                                isReadOnly={isLocked}
                                />
                                
                              </div>)
                            )} 
                        </DragIndexContext.Provider>
                      </SortableContext>
                      <DragOverlay>
                      <div
                        style={{
                          fontSize: '10px', 
                        }}
                      >
                      {`V${visits[visits.findIndex((i) => i === dragVisitIndex.active)]}`}
                      </div>
                    </DragOverlay>
                    </DndContext>
                  )}
                  {title !== "Visit number" && visits.map((item, idx) => (
                    <div className={`${styles.numCell} ${styles.td}`} key={idx}>
                      <div className={styles.VisitNumber}>
                        {title === "Visit window" && (
                          <span
                            className={styles.visitWindowContainer}
                            onClick={() => !isLocked && visitWindowHandler(item)}
                            role="button" // Adding a role for accessibility if not using a button element  
                            tabIndex={0} // Making the element focusable for keyboard users  
                            onKeyPress={(e) => {  
                              if (e.key === 'Enter' || e.key === ' ') {  
                                visitWindowHandler(item); // Allowing keyboard activation  
                              }  
                            }}  
                          >
                            <span className={styles.visitWindowItem}>
                              {getVisitWindowValues(windowUnit, idx, weekVisitWindow, dayVisitWindow) || "+/-"}                              
                            </span>
                          </span>
                        )}
                        {/* Display the correct rows based on the visitUnit */}  
                        {(title === "Days" || title === "Weeks") && (  
                          <div className="week-row-wrapper" style={{ flex: 1 }}>  
                            <div className="weeks-container">  
                              {(visitUnit === "Day" ? days : weeks)?.map((unit, unitIdx) => {  

                                // The result of the condition for border highlighting  
                                const isHighlighted = (idx === highlightWeekIndex ||  
                                                      (visitUnit === "Week" && weeksOutOfOrderIndices.includes(unitIdx)) ||  
                                                      (visitUnit === "Day" && daysOutOfOrderIndices.includes(unitIdx)));                                  
                          
                                return unitIdx === idx && (  
                                  <span key={unitIdx} className={styles.weekItem}>  
                                    <Input  
                                      className="td cell-input"  
                                      style={{  
                                        fontSize: "14px",  
                                        backgroundColor: "#FFF",  
                                        border: isHighlighted ? "5px solid rgb(255 182 0 / 27%)" : 'none',  
                                        textAlignLast: "center",  
                                      }}  
                                      key={`unit_${idx}`}  
                                      value={unit}  
                                      onChange={(e) => weekChangeHandler(e, idx)}  
                                      readOnly={isLocked}
                                    />  
                                  </span>  
                                );  
                              })}  
                            </div>  
                          </div>  
                        )}  
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <Collapse
          className="clearfix"
          style={{ width: collapseWidth ?? `${collapseWidth}px` }}
          activeKey={expandKeys}
          onChange={callback}
        >
          {visits &&
            panelMapping?.map((panel) => {
              return generatePanel(panel);
            })}
        </Collapse>
      </div>

      {visitWindowOpen?.visible && (
        <VisitWindowModal
          windowUnit={windowUnit}
          visitWindowOpen={visitWindowOpen}
          handleCancel={handleCancelWindow}
          handleSubmit={handleSubmitWindow}
          weekVisitWindow={windowUnit === 'Week' ? weekVisitWindow[visitWindowOpen.visit - 1] : dayVisitWindow[visitWindowOpen.visit - 1] }
        />
      )}
      
      <CopyVisitMappingModal
        visible={showVisitMappingModal}
        doCancel={doCancelVisitMapping}
        doConfirm={doConfirmVisitMapping}
        selectedVisitsOnMapping={selectedVisitMappingHandler}
        selectedPeriodsOnMapping={selectedPeriodMappingHandler}
      />

      {isOpenAddFootnoteModal.visible && (
        <AddFootnoteModal
          isOpen={isOpenAddFootnoteModal}
          onCancel={() => setIsOpenAddFootnoteModal({visible: false})}
        />
      )}
    </>
  );
}
